import { SyncLock } from "@mui/icons-material";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import { Divider, Stack } from "@mui/material";
import { usePrimaryBankingAccountMutation } from "Api/Mutations/Accounts/usePrimaryBankingAccountMutation";
import { useGetBankingAuthExpiration } from "Api/Queries/Banks/useGetBankingAuthExpiration";
import { ActionRow } from "Components/Shared/ActionRow";
import {
  areBankAccountActionsDisabled,
  isBankAccountDetailDisabled,
} from "Components/BankAccounts/BankAccountDetail/disabledAccountsUtils";
import { useBankingAuth } from "Hooks/useBankingAuth";
import { useBankingPispAuth } from "Hooks/useBankingPispAuth";
import type { BankAccountListItem } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { toArray } from "Utils/ArrayUtils";
import { formatDate } from "Utils/DateUtils";

type Props = {
  account: BankAccountListItem;
};

const RESOURCES = Resources.Pages.Content.BankAccounts.Detail;
const ACTIONS_RESOURCES = RESOURCES.Actions;

export const BankAccountActions = ({ account }: Props) => {
  const { t } = useResource();

  const { authorize, isPending: isAuthPending } = useBankingAuth();
  const { authorize: authorizePisp, isPending: isPispAuthPending } =
    useBankingPispAuth();
  const { mutate: savePrimaryAccount, isPending: isSetPrimaryPending } =
    usePrimaryBankingAccountMutation();
  const { data: expirationData } = useGetBankingAuthExpiration();

  const areActionsDisabled = areBankAccountActionsDisabled(account);
  const isAnyPending =
    isAuthPending || isPispAuthPending || isSetPrimaryPending;
  const isSetPrimaryDisabled =
    isAnyPending || isBankAccountDetailDisabled(account);

  const { environment, isPrimary, publicID } = account;
  const expirations = toArray(expirationData?.expirations);
  const expiration = expirations.find((e) => e.environment === environment);
  // The "Authorize payments" (PISP) row is shown only when a PISP token
  // already exists for this environment — i.e. payments were authorized before
  // and can be re-authorized here. First-time payment authorization is
  // intentionally NOT offered on this screen (it happens during the first payment),
  // so environments without a PISP token hide the row.
  const pispExpirationDate = expiration?.pispExpirationDate;

  const bankName = t(Resources.BankNames[account.bank]);

  const formatValidUntil = (date: string) =>
    t(ACTIONS_RESOURCES.AuthorizationValidUntil, {
      date: formatDate(date),
    });

  return (
    <Stack divider={<Divider sx={{ borderColor: "grey.100" }} />}>
      <ActionRow
        variant="primary"
        icon={isPrimary ? <StarIcon /> : <StarOutlineIcon />}
        title={t(
          isPrimary ? RESOURCES.IsPrimaryNote : ACTIONS_RESOURCES.SetAsPrimary,
        )}
        subtitle={t(ACTIONS_RESOURCES.PrimaryAccountDescription)}
        onClick={isPrimary ? undefined : () => savePrimaryAccount(publicID)}
        isDisabled={isSetPrimaryDisabled}
      />
      <ActionRow
        icon={<SyncLock />}
        title={t(ACTIONS_RESOURCES.AuthorizeAccess, { bankName })}
        subtitle={
          expiration?.aisExpirationDate
            ? formatValidUntil(expiration.aisExpirationDate)
            : undefined
        }
        onClick={() => authorize({ environment })}
        isDisabled={isAnyPending || areActionsDisabled}
      />
      {pispExpirationDate && (
        <ActionRow
          icon={<CreditCardOutlinedIcon />}
          title={t(ACTIONS_RESOURCES.AuthorizePayments, { bankName })}
          subtitle={formatValidUntil(pispExpirationDate)}
          onClick={() => authorizePisp({ environment })}
          isDisabled={isAnyPending || areActionsDisabled}
        />
      )}
    </Stack>
  );
};
